import { Link, NavLink } from "react-router-dom";
import { useState } from "react";
import { List, X } from "@phosphor-icons/react";
import { BUSINESS } from "./business";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/features", label: "Features" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About Us" },
  { to: "/contact", label: "Contact Us" },
];

export default function MarketingLayout({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      {/* Header */}
      <header className="border-b-2 border-black bg-[#FDFBF7] sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="marketing-logo">
            <img src="/icons/icon-192.png?v=4" alt="DueMate" className="w-9 h-9 rounded-md border-2 border-black shadow-[3px_3px_0px_#111111] object-cover" />
            <span className="font-heading font-black text-xl tracking-tight">DueMate</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV.map(({ to, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md font-bold text-sm ${isActive ? "bg-[#111111] text-white" : "text-[#111111] hover:bg-[#F3F0E9]"}`
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <Link to="/login" data-testid="nav-login" className="nb-btn bg-white px-4 py-2 text-sm">Login</Link>
            <Link to="/login" data-testid="nav-get-started" className="nb-btn bg-[#4361EE] text-white px-4 py-2 text-sm">Get Started</Link>
          </div>

          <button data-testid="mobile-menu-toggle" className="md:hidden nb-btn bg-white p-2" onClick={() => setOpen(!open)}>
            {open ? <X size={20} weight="bold" /> : <List size={20} weight="bold" />}
          </button>
        </div>

        {open && (
          <div className="md:hidden border-t-2 border-black bg-[#FDFBF7] px-4 py-3 space-y-1">
            {NAV.map(({ to, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block px-3 py-2 rounded-md font-bold text-sm ${isActive ? "bg-[#111111] text-white" : "text-[#111111]"}`
                }
              >
                {label}
              </NavLink>
            ))}
            <div className="flex gap-2 pt-2">
              <Link to="/login" onClick={() => setOpen(false)} className="nb-btn bg-white flex-1 text-center px-4 py-2 text-sm">Login</Link>
              <Link to="/login" onClick={() => setOpen(false)} className="nb-btn bg-[#4361EE] text-white flex-1 text-center px-4 py-2 text-sm">Get Started</Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="border-t-2 border-black bg-white mt-16">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-10 grid gap-8 md:grid-cols-4">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <img src="/icons/icon-192.png?v=4" alt="DueMate" className="w-9 h-9 rounded-md border-2 border-black object-cover" />
              <span className="font-heading font-black text-xl">DueMate</span>
            </div>
            <p className="text-sm text-[#4A4A4A] font-semibold max-w-md">
              A SaaS platform that helps small businesses manage customer service reminders, due dates and follow-ups — so they never miss a return visit.
            </p>
          </div>

          <div>
            <p className="text-xs font-bold tracking-[0.2em] uppercase mb-3">Product</p>
            <ul className="space-y-2 text-sm font-semibold">
              <li><Link to="/features" className="hover:underline">Features</Link></li>
              <li><Link to="/pricing" className="hover:underline">Pricing</Link></li>
              <li><Link to="/about" className="hover:underline">About Us</Link></li>
              <li><Link to="/contact" className="hover:underline">Contact Us</Link></li>
            </ul>
          </div>

          <div>
            <p className="text-xs font-bold tracking-[0.2em] uppercase mb-3">Legal</p>
            <ul className="space-y-2 text-sm font-semibold">
              <li><Link to="/privacy-policy" data-testid="footer-privacy" className="hover:underline">Privacy Policy</Link></li>
              <li><Link to="/terms-and-conditions" data-testid="footer-terms" className="hover:underline">Terms &amp; Conditions</Link></li>
              <li><Link to="/refund-and-cancellation" data-testid="footer-refund" className="hover:underline">Refund &amp; Cancellation</Link></li>
              <li><Link to="/data-deletion" data-testid="footer-data-deletion" className="hover:underline">Data Deletion</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t-2 border-black">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-5 grid gap-3 md:grid-cols-[1fr_auto] md:items-start text-xs font-semibold text-[#4A4A4A]">
            <div className="space-y-1 leading-relaxed" data-testid="footer-business-info">
              <p>
                <strong className="text-[#111111]">{BUSINESS.legalName}</strong> ({BUSINESS.organizationType}) · Operated by {BUSINESS.ownerName}
              </p>
              <p>
                {BUSINESS.address.formatted} ·{" "}
                <a href={`tel:${BUSINESS.phone}`} className="hover:underline text-[#111111]">{BUSINESS.phoneDisplay}</a> ·{" "}
                <a href={`mailto:${BUSINESS.email}`} className="hover:underline text-[#111111]">{BUSINESS.email}</a>
              </p>
              <p>
                Udyam Registration: <span className="font-mono text-[#111111]">{BUSINESS.udyamNumber}</span>
              </p>
            </div>
            <p className="md:text-right">© {new Date().getFullYear()} {BUSINESS.legalName}. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
